import MaterialTable from 'material-table';
import { defaultGridOptions, tooltipDelay, useGridColDefs } from '../../utils/GridUtils';
import { Button, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { centeredCell } from '../../constants/StyleConstants';
import AnimatedButton from '../../components/UI/AnimatedButton';
import { useContext, useEffect, useState } from 'react';
import { LoginContext } from '../../contexts/LoginContext';
import FormatListNumberedOutlinedIcon from '@material-ui/icons/FormatListNumberedOutlined';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { Fragment } from 'react';
import LogisticsConfirmationModal from '../Logistics/LogisticsConfirmationModal';
import { AppContext } from '../../contexts/AppContext';
import clsx from 'clsx';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { SORT } from './ScrapsPresenter';

const useStyles = makeStyles(theme => ({
  centeredCell,
  root: {
    marginTop: '3em',
  },
  gridActions: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: '1em',
  },
  gridControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  gridButtonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '1em',
  },
  gridReference: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    columnGap: '1em',
    fontSize: '.8em',
  },
  reference: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  leftSideButton: {
    borderRadius: '1em',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  rightSideButton: {
    borderRadius: '1em',
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  activeGlow: {
    color: 'rgb(0, 255, 0)',
    boxShadow: '0px 0px 10px 0px rgb(0, 255, 0, .5)',
    borderRadius: '50%',
    margin: 0,
    stroke: 'rgb(0, 255, 0, .5)',
    strokeWidth: 4,
    strokeOpacity: 0.7,
  },
  hasNoComments: {
    color: '#BDBDBD',
  },
  sortButton: {
    '&.MuiButton-root': {
      minWidth: 'unset',
    },
    '&.MuiButton-startIcon': {
      margin: 0,
    },
  },
}));


const SortButtons = ({ sort, setSort }) => {
  const classes = useStyles();

  return (
    <div>
      <span style={{ fontSize: '.8em' }}>Ordenar por Fecha </span>
      <Button
        className={clsx(classes.leftSideButton, classes.sortButton)}
        variant={sort === SORT.DOWNWARD ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={() => (sort === SORT.DOWNWARD ? setSort(SORT.NONE) : setSort(SORT.DOWNWARD))}>
        <ArrowDownwardIcon style={{ fontSize: '1em' }} />
      </Button>
      <Button
        stlye={{ padding: 0 }}
        className={clsx(classes.rightSideButton, classes.sortButton)}
        variant={sort === SORT.UPWARD ? 'contained' : 'outlined'}
        color="primary"
        size="small"
        onClick={() => (sort === SORT.UPWARD ? setSort(SORT.NONE) : setSort(SORT.UPWARD))}>
        <ArrowUpwardIcon style={{ fontSize: '1em' }} />
      </Button>
    </div>
  );
};

const GridActions = ({
  selectedRows,
  setOpen,
  handleExport,
  loadingExport,
  sort,
  setSort,
  rows,
  ...restProps
}) => {
  const classes = useStyles();
  const { isAllowedByRole } = useContext(LoginContext);
  const [loadingExportSelection, setLoadingExportSelection] = useState(false);
  const [loadingExportAll, setLoadingExportAll] = useState(false);

  useEffect(() => {
    !loadingExport && setLoadingExportSelection(false);
    !loadingExport && setLoadingExportAll(false);
  }, [loadingExport]);

  const exportSelection = () => {
    setLoadingExportSelection(true);
    handleExport(selectedRows.map(r => r.delivery));
  };

  const exportAll = () => {
    setLoadingExportAll(true);
    handleExport();
  };

return  (
  <div className={classes.gridControls}>
      <div className={classes.gridButtonsWrapper}>
        <AnimatedButton
          disabled={selectedRows.isEmpty()}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<FormatListNumberedOutlinedIcon />}
          onClick={() => isAllowedByRole() && exportSelection()}
          loading={loadingExportSelection}>
          Exportar selección
        </AnimatedButton>
        <AnimatedButton
          disabled={rows.isEmpty()}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<ListAltIcon />}
          onClick={() => isAllowedByRole() && exportAll()}
          loading={loadingExportAll}>
          Exportar todo
        </AnimatedButton>
       
      </div>
      <SortButtons sort={sort} setSort={setSort} />
    </div>
  );
};

const ScrapsGrid = ({
  data,
  loading,
  tableRef,
  reloadGrid,
  selectedRows,
  setSelectedRows,
  open,
  setOpen,
  gridActions,
}) => {
  const classes = useStyles();

  const BoldValue = ({ value }) => {
    return (<b>{value}</b>);
  };
  
  const colDefs = useGridColDefs([

    { title: 'Contrato', field: 'contract'},
    { title: 'Entrega', field: 'delivery', valueRender: (value, rowData) => <BoldValue value={value} />  },
    { title: 'Nota Entrega', field: 'deliveryNote' },
    { title: 'Fecha', field: 'deliveryDate', valueType: 'date' },
    { title: 'Código Proveedor', field: 'supplierCode' },
    { title: 'Nombre Proveedor', field: 'supplierName' },
    { title: 'Código Material', field: 'materialCode', valueRender: (value) => value.replace(/^0+/, "") },
    { title: 'Nombre Material', field: 'materialName' },
    { title: 'Cantidad', field: 'amount', valueType: 'numeric' },
    { title: 'Unidad Medida', field: 'unitMeasure' },
    { title: 'Incoterms', field: 'incoterms' },
    { title: 'Transporte', field: 'transport' },
    { title: 'Chofer', field: 'driver' },
    { title: 'Camion', field: 'truck' },
    { title: 'Acoplado', field: 'trailer' },
    { title: 'KG Bruto', field: 'grossWeightKG', valueType: 'numeric' },
    { title: 'KG Tara', field: 'taraKG', valueType: 'numeric' },
    { title: 'KG Descuento', field: 'discountedWeightKG', valueType: 'numeric' },
    { title: 'Descuento', field: 'discount', valueType: 'numeric' },
    { title: 'KG Descargados', field: 'unloadedWeightKG', valueType: 'numeric' },
    { 
      title: 'Observaciones', 
      field: 'observation',
      valueRender: (value, rowData) =>{
        return (
        <Tooltip title = {rowData.observation} enterDelay={tooltipDelay} enterNextDelay={tooltipDelay} style={{ overflow: 'visible', maxWidth: 'none' }}>
          <span>
            <BoldValue value = {rowData.observation}/>
          </span>
        </Tooltip>
        )
      }
    }
  ]);

  return (
    <Fragment>
      <LogisticsConfirmationModal
        open={open}
        setOpen={setOpen}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        reloadGrid={reloadGrid}
      />
      
      <div className={classes.root}>
        <div className={classes.gridActions}>
          <GridActions selectedRows={selectedRows} loading={loading} setOpen={setOpen} rows={data} {...gridActions} />
        </div>
        <MaterialTable
          {...defaultGridOptions}
          options={{
            ...defaultGridOptions.options,
            pageSizeOptions: [...defaultGridOptions.options.pageSizeOptions, defaultGridOptions.options.pageSizeOptions.last() * 2],
            selection: true,
            headerSelectionProps: { color: 'primary'}, 
            selectionProps: rowData => {
              return {
                color: 'primary',
              };
            },
          }}
          onSelectionChange={(rows, row) => setSelectedRows(rows)}
          columns={colDefs}
          data={data}
          isLoading={loading}
          tableRef={tableRef}
        />
      </div>
    </Fragment>
  );
};

export default ScrapsGrid;